import { ActionConfirmation, ActionConfirmationHost, usePublishActionOutcome } from "../ui/ActionConfirmation.js";
import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { confirmStudentRecord, get, sendHubInvitations, type StudentDetail } from "../api.js";
import { isOwner, useAuth } from "../auth.js";
import { formatDate, formatDuration } from "../format.js";
import { Pagination } from "../Pagination.js";
import { CARD_RECORD_PAGE_SIZE } from "../paginationPageSizes.js";
import { useAsync } from "../useAsync.js";
import { ScrollableTabs } from "../ui/Tabs.js";
import { NoteRecipients } from "../NoteRecipients.js";
import { PracticeAttachments } from "../PracticeAttachments.js";
import { StudentNeckDiagrams } from "../StudentNeckDiagrams.js";
import { StudentGrooves } from "../StudentGrooves.js";
import { BookingPayer } from "./BookingPayer.js";
import { StudentDetails } from "./StudentDetails.js";
import { ManageLessonRow } from "../booking/ManageLessonRow.js";
import { CreditCorrections } from "../booking/CreditCorrections.js";
import { canUseCreditCorrectionConsole } from "../booking/creditCorrectionModel.js";
import { TeachingAssignments } from "../booking/TeachingAssignments.js";
import { StudentLessonHistory } from "./StudentLessonHistory.js";
import { StudentLessonManager } from "./StudentLessonManager.js";
import { StudentProgress } from "./StudentProgress.js";
import { StudentRecordBooking } from "./StudentRecordBooking.js";
import { StudentOverviewCredits, canViewStudentOverviewCredits } from "./StudentOverviewCredits.js";
import { StudentCreditReadProvider, useStudentCreditReads } from "../booking/StudentCreditReads.js";
import { StudentRecordNotes } from "./StudentRecordNotes.js";
import { TutorPracticeTodos } from "./TutorPracticeTodos.js";
import { TutorStudentTools } from "./TutorStudentTools.js";
import { logPaymentHref, paymentWorkflowAccess } from "../booking/paymentWorkflow.js";
import { HubAccessPanel } from "../HubAccessPanel.js";
import { StudentActivityPanel } from "../engagement/ActivityPanels.js";
import { HubInviteButton } from "../HubInviteButton.js";
import { FamilyMembers } from "./FamilyMembers.js";
import { StudentRecurringLessons } from "./StudentRecurringLessons.js";
import { AdminFamilyOverview } from "./AdminFamilyOverview.js";
import { ParentContactDetails } from "./ParentContactDetails.js";
import { StudentUpcomingLessons } from "./StudentUpcomingLessons.js";

export type StudentRecordTab =
  | "overview"
  | "lessons"
  | "practice"
  | "progress"
  | "admin";

const STUDENT_RECORD_TABS: Array<{ id: StudentRecordTab; label: string }> = [
  { id: "overview", label: "Overview" },
  { id: "lessons", label: "Lessons" },
  { id: "practice", label: "Practice" },
  { id: "progress", label: "Progress" },
  { id: "admin", label: "Admin" },
];

const UPCOMING_LESSON_PAGE_SIZE = CARD_RECORD_PAGE_SIZE;

type StudentPracticePage = {
  sessions: StudentDetail["recentPractice"];
  page: number;
  limit: number;
  hasMore: boolean;
};

export function resolveStudentRecordTab(
  requested: string | null,
): StudentRecordTab {
  // Preserve old links/bookmarks from the former Payments & credits tab.
  if (requested === "credits") return "admin";
  return STUDENT_RECORD_TABS.some((tab) => tab.id === requested)
    ? (requested as StudentRecordTab)
    : "overview";
}

export function studentRecordTabForKey(
  current: StudentRecordTab,
  key: string,
): StudentRecordTab | null {
  const currentIndex = STUDENT_RECORD_TABS.findIndex(
    (tab) => tab.id === current,
  );
  if (currentIndex < 0) return null;

  if (key === "Home") return STUDENT_RECORD_TABS[0]?.id ?? null;
  if (key === "End") return STUDENT_RECORD_TABS.at(-1)?.id ?? null;
  if (key !== "ArrowLeft" && key !== "ArrowRight") return null;

  const direction = key === "ArrowRight" ? 1 : -1;
  const nextIndex =
    (currentIndex + direction + STUDENT_RECORD_TABS.length) %
    STUDENT_RECORD_TABS.length;
  return STUDENT_RECORD_TABS[nextIndex]?.id ?? null;
}

export function formatUpcomingLessonDuration(
  startsAt: string,
  endsAt: string,
): string | null {
  const durationMs = Date.parse(endsAt) - Date.parse(startsAt);
  if (!Number.isFinite(durationMs) || durationMs <= 0) return null;
  const minutes = Math.round(durationMs / 60_000);
  return minutes > 0 ? `${minutes} min` : null;
}

export function tutorForUpcomingLesson(
  serviceName: string | null,
  enrolments: StudentDetail["enrolments"],
): string | null {
  const relevant =
    serviceName === null
      ? enrolments
      : enrolments.filter((enrolment) => enrolment.serviceName === serviceName);
  const tutorNames = [
    ...new Set(
      relevant
        .map((enrolment) => enrolment.tutorName)
        .filter(
          (name): name is string => typeof name === "string" && name.length > 0,
        ),
    ),
  ];
  return tutorNames.length === 1 ? (tutorNames[0] ?? null) : null;
}

/**
 * Whether the record's upcoming lessons offer Move and Cancel.
 *
 * Every member of staff on the booking system gets the row actions; FastAPI
 * decides per lesson whether this person may move or cancel it. A wider grant
 * (`appointments.manage.all`) used to *remove* them, which left a tutor given
 * extra authority with less on their own pupils' lessons than before (audit
 * PERM-09).
 */
export function canManageStudentRecordLessons(bookingStaff: boolean): boolean {
  return bookingStaff;
}

function isLessonReturnPath(requested: string | null): requested is string {
  return (
    requested !== null &&
    /^\/bookings\/[A-Za-z0-9_-]{1,128}\/lesson(?:\?[^#]*)?$/.test(requested)
  );
}

export function studentDirectoryReturnPath(
  requested: string | null,
  mine: boolean,
): string {
  const fallback = mine ? "/students?scope=mine" : "/students";
  if (requested === null) return fallback;
  if (requested === "/students" || requested.startsWith("/students?")) return requested;
  return isLessonReturnPath(requested) ? requested : fallback;
}

export function studentDirectoryReturnLabel(
  requested: string | null,
  mine: boolean,
): string {
  if (isLessonReturnPath(requested)) return "Back to lesson";
  return mine ? "My students" : "Students";
}

export function StudentDirectoryBackLink({
  requested,
  mine,
  state,
}: {
  requested: string | null;
  mine: boolean;
  state: unknown;
}) {
  return (
    <p className="small muted student-record-back">
      <Link to={studentDirectoryReturnPath(requested, mine)} state={state}>
        ← {studentDirectoryReturnLabel(requested, mine)}
      </Link>
    </p>
  );
}

function contactHref(student: StudentDetail["student"]): string | null {
  const email =
    student.studentEmail ?? student.parentEmail ?? student.secondaryEmail;
  if (email) return `mailto:${encodeURIComponent(email)}`;

  const phone = student.parentPhone?.trim();
  if (!phone) return null;
  const dialable = phone.replace(/[^\d+]/g, "");
  return dialable.length > 0 ? `tel:${dialable}` : null;
}

function payerLabel(payer: StudentDetail["student"]["billingPayer"]): string {
  if (payer === "family") return "Family";
  if (payer === "self") return "Self";
  if (payer === "school") return "School";
  return "Not stated";
}

export function Student() {
  const { id = "" } = useParams();
  const { me } = useAuth();
  const accessKey = JSON.stringify([id, me?.user?.id, me?.roles, me?.capabilities, me?.booking]);
  return <ActionConfirmationHost key={id}><StudentCreditReadProvider key={accessKey} studentId={id}><StudentContent /></StudentCreditReadProvider></ActionConfirmationHost>;
}

function StudentContent() {
  const { id = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { me } = useAuth();
  const creditReads = useStudentCreditReads();
  const [searchParams, setSearchParams] = useSearchParams();
  const mine = searchParams.get("scope") === "mine";
  const activeTab = resolveStudentRecordTab(searchParams.get("tab"));
  const bookingRequested = searchParams.get("book") === "1";
  const { data, error, loading, reload } = useAsync(
    () => get<StudentDetail>(`/admin/students/${id}`),
    [id],
  );

  /** Booking payer reads separately, so record edits bump a revision as well as reloading. */
  const [corrections, setCorrections] = useState(0);
  const [creditRevision, setCreditRevision] = useState(0);
  const [practiceRevision, setPracticeRevision] = useState(0);
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [practiceSessions, setPracticeSessions] = useState<
    StudentDetail["recentPractice"]
  >([]);
  const [practicePage, setPracticePage] = useState(1);
  const [practiceHasMore, setPracticeHasMore] = useState(false);
  const [practiceLoading, setPracticeLoading] = useState(false);
  const [practiceError, setPracticeError] = useState<string | null>(null);
  const [focusedTab, setFocusedTab] = useState<StudentRecordTab>(activeTab);
  const [creationNotice, setCreationNotice] = useState<string | null>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    setFocusedTab(activeTab);
  }, [activeTab]);

  useEffect(() => {
    const state = location.state as { hubAccessNotice?: unknown } | null;
    if (typeof state?.hubAccessNotice !== "string" || state.hubAccessNotice === "") return;
    setCreationNotice(state.hubAccessNotice);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.key, location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    setUpcomingPage(1);
  }, [id]);

  useEffect(() => {
    setPracticeSessions(data?.recentPractice ?? []);
    setPracticePage(1);
    setPracticeHasMore(
      data?.recentPracticeHasMore === true ||
        (data?.recentPracticeHasMore === undefined &&
          (data?.recentPractice.length ?? 0) === 10),
    );
    setPracticeError(null);
  }, [data]);

  useEffect(() => {
    if (!data || !bookingRequested || activeTab !== "lessons") return;
    const target = document.getElementById("student-record-booking-tools");
    if (!(target instanceof HTMLElement)) return;

    target.focus({ preventScroll: true });
    target.scrollIntoView?.({ block: "start" });

    // `book=1` is a one-shot focus request, not durable record state. Removing
    // it with replace keeps browser Back/Forward on meaningful workspace steps.
    const next = new URLSearchParams(searchParams);
    next.delete("book");
    setSearchParams(next, { replace: true, state: location.state });
  }, [
    activeTab,
    bookingRequested,
    data,
    location.state,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    if (activeTab !== "overview" || location.hash !== "#student-family-overview" || data?.student.id !== id) return;
    const target = document.getElementById("student-family-overview");
    target?.scrollIntoView?.({ block: "start" });
    target?.focus({ preventScroll: true });
  }, [activeTab, location.hash, id, data?.student.id]);

  if (loading && !data) return <p className="empty">Loading…</p>;
  if (error && !data) return <div className="error">{error}</div>;
  if (!data) return null;

  const { student, enrolments, upcomingBookings } = data;
  const { ameliaFutureLessons } = data;
  const owner = isOwner(me);
  const tutorRole = me?.roles.includes("tutor") === true;
  const canManageOtherTutors =
    me?.booking.staff === true &&
    (owner || me.capabilities.includes("appointments.manage.all"));
  const canManageStudentLesson = canManageStudentRecordLessons(me?.booking.staff === true);
  const canUseCreditCorrections =
    me?.booking.staff === true &&
    canUseCreditCorrectionConsole(
      me.capabilities,
      owner,
      me.booking.providerId,
    );
  const showTutorTools = me?.booking.staff === true && tutorRole && !owner;
  const showPracticeTodos = owner || tutorRole;
  const upcomingPages = Math.max(
    1,
    Math.ceil(upcomingBookings.length / UPCOMING_LESSON_PAGE_SIZE),
  );
  const currentUpcomingPage = Math.min(upcomingPage, upcomingPages);
  const visibleUpcomingBookings = upcomingBookings.slice(
    (currentUpcomingPage - 1) * UPCOMING_LESSON_PAGE_SIZE,
    currentUpcomingPage * UPCOMING_LESSON_PAGE_SIZE,
  );
  const studentContactHref = contactHref(student);
  const hasHubAccess = "studentAccount" in student.hubAccess
    ? student.hubAccess.studentAccount !== null || student.hubAccess.familyAccounts.length > 0
    : student.hubAccess.studentAccountLinked || student.hubAccess.familyAccountCount > 0;
  const canReadFamilyLessons = owner && (student.familyOverviewAvailable ??
    ("familyAccounts" in student.hubAccess && student.hubAccess.familyAccounts.length > 0));
  const hasAttention =
    student.status !== "active" ||
    upcomingBookings.length === 0 ||
    !hasHubAccess;

  function tutorStudentChanged() {
    creditReads.invalidate();
    setCreditRevision((revision) => revision + 1);
    setCorrections((revision) => revision + 1);
    reload();
  }

  async function loadOlderPractice() {
    if (practiceLoading || !practiceHasMore) return;
    setPracticeLoading(true);
    setPracticeError(null);
    try {
      const nextPage = practicePage + 1;
      const result = await get<StudentPracticePage>(
        `/admin/students/${id}/practice/sessions`,
        { page: String(nextPage), limit: "10" },
      );
      setPracticeSessions((current) => {
        const seen = new Set(current.map((session) => session.id));
        return [
          ...current,
          ...result.sessions.filter((session) => !seen.has(session.id)),
        ];
      });
      setPracticePage(result.page);
      setPracticeHasMore(result.hasMore);
    } catch (cause: unknown) {
      setPracticeError(
        cause instanceof Error ? cause.message : "Could not load older practice.",
      );
    } finally {
      setPracticeLoading(false);
    }
  }

  function chooseTab(tab: StudentRecordTab) {
    if (tab === activeTab) return;
    const next = new URLSearchParams(searchParams);
    next.delete("book");
    if (tab === "overview") next.delete("tab");
    else next.set("tab", tab);
    setSearchParams(next, { state: location.state });
  }

  function openBookingTools() {
    const next = new URLSearchParams(searchParams);
    next.set("tab", "lessons");
    next.set("book", "1");
    setSearchParams(next, { state: location.state });
  }

  function onTabKeyDown(
    event: ReactKeyboardEvent<HTMLButtonElement>,
    currentTab: StudentRecordTab,
  ) {
    const nextTab = studentRecordTabForKey(currentTab, event.key);
    if (nextTab === null) return;
    event.preventDefault();
    const nextIndex = STUDENT_RECORD_TABS.findIndex(
      (tab) => tab.id === nextTab,
    );
    setFocusedTab(nextTab);
    tabRefs.current[nextIndex]?.focus();
  }

  return (
    <div className={`student-record-page${activeTab === "progress" ? " student-record-page--progress" : ""}`}>
      {error && <p className="error" role="alert">Could not refresh this record: {error}</p>}
      <StudentDirectoryBackLink
        requested={searchParams.get("return")}
        mine={mine}
        state={location.state}
      />

      <header className="student-record-head">
        <div className="student-record-identity">
          <div className="student-record-title-row">
            <div>
              <h1>{student.displayName ?? student.fullName}</h1>
              <p className="lede">
                {enrolments.length > 0
                  ? enrolments
                      .map(
                        (enrolment) =>
                          `${enrolment.instrument ?? enrolment.serviceName ?? "Lessons"} with ${
                            enrolment.tutorName ?? "nobody assigned"
                          }`,
                      )
                      .join(" · ")
                  : (student.instrument ?? "No teaching assignment")}
              </p>
            </div>
            {student.status !== "active" && (
              <span className="tag warn">{student.status}</span>
            )}
          </div>
          <div className="student-record-context small muted">
            <span>
              {hasHubAccess ? "Hub access linked" : "No Hub account linked"}
            </span>
            {student.parentName && <span>Contact: {student.parentName}</span>}
            <span>Billing payer: {payerLabel(student.billingPayer)}</span>
          </div>
          <ParentContactDetails email={student.parentEmail} phone={student.parentPhone} />
        </div>

        <StudentUpcomingLessons studentId={id} familyEnabled={canReadFamilyLessons}
          familyHref={`/students/${encodeURIComponent(id)}#student-family-overview`}
          canManage={canManageStudentLesson} refreshRevision={corrections} onChanged={tutorStudentChanged}
          lessons={(data.nextWeekBookings ?? upcomingBookings).map(lesson => ({
            id: lesson.appointmentId, studentName: student.fullName, startsAt: lesson.startsAt,
            endsAt: lesson.endsAt, serviceName: lesson.serviceName, status: lesson.status,
            tutorName: tutorForUpcomingLesson(lesson.serviceName, enrolments), school: lesson.isSchools,
            ameliaAppointmentId: lesson.ameliaAppointmentId,
          }))} />

        <div className="student-record-actions" aria-label="Student actions">
          <HubInviteButton key={id} studentName={student.fullName} invitations={student.hubInvitations}
            sendInvitations={(recipients, resend) => sendHubInvitations(id, recipients, resend)} onChanged={reload} />
          {me?.booking.staff === true ? (
            <button
              type="button"
              className="student-record-book-action"
              onClick={openBookingTools}
            >
              Book lesson
            </button>
          ) : (
            <span className="stack" title="Booking tools are not available.">
              <button
                type="button"
                className="student-record-book-action"
                disabled
              >
                Book lesson
              </button>
              <span className="small muted">
                Booking tools are not available.
              </span>
            </span>
          )}
          {(paymentWorkflowAccess(me).lessons || paymentWorkflowAccess(me).blocks) && (
            <Link className="student-record-payment-action" to={logPaymentHref(student.id)}>Log payment</Link>
          )}
          {studentContactHref && (
            <a
              className="student-record-contact-action"
              href={studentContactHref}
            >
              Contact
            </a>
          )}
        </div>
      </header>

      <ScrollableTabs
        className="student-record-tabs"
        ariaLabel="Student record sections"
      >
        {STUDENT_RECORD_TABS.map((tab, index) => (
          <button
            className="student-record-tab"
            type="button"
            role="tab"
            id={`student-record-tab-${tab.id}`}
            aria-selected={activeTab === tab.id}
            aria-controls={`student-record-panel-${tab.id}`}
            tabIndex={focusedTab === tab.id ? 0 : -1}
            key={tab.id}
            ref={(node) => {
              tabRefs.current[index] = node;
            }}
            onFocus={() => setFocusedTab(tab.id)}
            onKeyDown={(event) => onTabKeyDown(event, tab.id)}
            onClick={() => chooseTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </ScrollableTabs>

      {STUDENT_RECORD_TABS.map((tab) => (
        <section
          className="student-record-panel"
          id={`student-record-panel-${tab.id}`}
          role="tabpanel"
          aria-labelledby={`student-record-tab-${tab.id}`}
          hidden={activeTab !== tab.id}
          key={tab.id}
        >
          {activeTab === tab.id && (
            <>
              {activeTab === "overview" && (
                <>
                  {owner && student.addedByName && (
                    <UncheckedRecord
                      studentId={id}
                      addedByName={student.addedByName}
                      onConfirmed={reload}
                    />
                  )}

                  <StudentRecurringLessons studentId={id} studentName={student.fullName} refreshRevision={creditRevision} />

                  <FamilyMembers studentId={id} onChanged={tutorStudentChanged} />

                  {canReadFamilyLessons && <AdminFamilyOverview key={`family:${id}`} studentId={id} refreshRevision={corrections} />}

                  {hasAttention && (
                    <section
                      className="panel student-record-attention"
                      aria-labelledby="student-record-attention-heading"
                    >
                      <h2 id="student-record-attention-heading">
                        Needs attention
                      </h2>
                      <ul>
                        {student.status !== "active" && (
                          <li>Student status is {student.status}.</li>
                        )}
                        {upcomingBookings.length === 0 && (
                          <li>No upcoming lesson is booked.</li>
                        )}
                        {!hasHubAccess && (
                          <li>No Hub account is linked yet.</li>
                        )}
                      </ul>
                    </section>
                  )}

                  {canViewStudentOverviewCredits(me) && (
                    <StudentOverviewCredits key={id} studentId={id} refreshRevision={creditRevision} />
                  )}

                  <StudentLessonManager studentId={id} studentName={student.fullName} onChanged={tutorStudentChanged} />

                  <div className="student-record-overview-grid">
                    <StudentActivityPanel id={id} />
                    <section
                      className="panel"
                      aria-labelledby="student-record-teaching-heading"
                    >
                      <h2 id="student-record-teaching-heading">Teaching</h2>
                      {enrolments.length > 0 ? (
                        <ul className="student-record-plain-list">
                          {enrolments.map((enrolment, index) => (
                            <li
                              key={`${
                                enrolment.serviceName ??
                                enrolment.instrument ??
                                "lesson"
                              }:${index}`}
                            >
                              <strong>
                                {enrolment.instrument ??
                                  enrolment.serviceName ??
                                  "Lessons"}
                              </strong>
                              <span className="small muted">
                                {enrolment.tutorName ?? "Nobody assigned"}
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="empty">
                          No teaching assignment is recorded.
                        </p>
                      )}
                    </section>

                    <section
                      className="panel"
                      aria-labelledby="student-record-context-heading"
                    >
                      <h2 id="student-record-context-heading">
                        Student context
                      </h2>
                      <dl className="student-record-context-list">
                        <div>
                          <dt>Portal</dt>
                          <dd>
                            {hasHubAccess ? "Linked" : "Not linked"}
                          </dd>
                        </div>
                        <div>
                          <dt>Billing payer</dt>
                          <dd>{payerLabel(student.billingPayer)}</dd>
                        </div>
                        {student.parentName && (
                          <div>
                            <dt>Parent / contact</dt>
                            <dd>{student.parentName}</dd>
                          </div>
                        )}
                        {student.school && (
                          <div>
                            <dt>School</dt>
                            <dd>
                              {student.school}
                              {student.formClass
                                ? ` · ${student.formClass}`
                                : ""}
                            </dd>
                          </div>
                        )}
                      </dl>
                    </section>
                  </div>
                </>
              )}

              {activeTab === "lessons" && (
                <>
                  {me?.booking.staff === true && (
                    <div
                      id="student-record-booking-tools"
                      className="student-record-booking-tools"
                      role="region"
                      aria-label={`Book lesson for ${student.fullName}`}
                      tabIndex={-1}
                    >
                      <StudentRecordBooking
                        studentId={id}
                        studentName={student.fullName}
                        canManageOtherTutors={canManageOtherTutors}
                        onChanged={tutorStudentChanged}
                      />
                    </div>
                  )}

                  <div className="student-record-section-head">
                    <h2>Upcoming lessons</h2>
                    {me?.booking.staff && ameliaFutureLessons > 0 && (
                      <div className="student-record-bulk-actions">
                        <Link to={`/students/${id}/move-future`}>
                          Move all future lessons
                        </Link>
                        <Link to={`/students/${id}/cancel-future`}>
                          Cancel all future lessons
                        </Link>
                      </div>
                    )}
                  </div>

                  <div className="panel student-record-list-panel">
                    {upcomingBookings.length === 0 && (
                      <p className="empty">No upcoming lessons.</p>
                    )}
                    {visibleUpcomingBookings.map((booking) => {
                      const duration = formatUpcomingLessonDuration(
                        booking.startsAt,
                        booking.endsAt,
                      );
                      return (
                        <ManageLessonRow
                          key={booking.appointmentId}
                          appointmentId={booking.ameliaAppointmentId}
                          canManage={canManageStudentLesson}
                          studentName={student.fullName}
                          startsAt={booking.startsAt}
                          serviceName={booking.serviceName}
                          onChanged={tutorStudentChanged}
                        >
                          <div className="small muted">
                            {booking.serviceName ?? "Lesson"}
                            {duration ? ` · ${duration}` : ""}
                            {booking.isSchools && " · schools"}
                          </div>
                        </ManageLessonRow>
                      );
                    })}
                    <Pagination
                      page={currentUpcomingPage}
                      pages={upcomingPages}
                      onPageChange={setUpcomingPage}
                      ariaLabel="Upcoming lessons pagination"
                    />
                  </div>

                  <StudentRecordNotes
                    studentId={id}
                    studentName={student.fullName}
                  />

                  <StudentLessonHistory studentId={id} />
                </>
              )}

              {activeTab === "practice" && (
                <>
                  <div className="student-record-section-head">
                    <div>
                      <h2>Practice</h2>
                      <p className="small muted">
                        Assigned work and the student’s recent practice history.
                      </p>
                    </div>
                  </div>

                  {showPracticeTodos && <TutorPracticeTodos studentId={id} refreshVersion={practiceRevision} />}
                  <StudentNeckDiagrams key={id} studentId={id} />

                  <div className="student-record-section-head student-record-practice-history-head">
                    <h2>Recent practice</h2>
                  </div>
                  {practiceSessions.length === 0 ? (
                    <div className="panel">
                      <p className="empty">No practice has been logged yet.</p>
                    </div>
                  ) : (
                    practiceSessions.map((session) => (
                      <div className="panel session" key={session.id}>
                        <div
                          className="row"
                          style={{ alignItems: "baseline", flexWrap: "wrap" }}
                        >
                          <span className="tabular small muted">
                            {formatDate(session.startedAt)}
                          </span>
                          <strong className="tabular">
                            {formatDuration(session.durationSeconds)}
                          </strong>
                          <span style={{ flex: 1 }}>
                            {session.name ?? <span className="muted">—</span>}
                            {session.needsWork && (
                              <span className="tag warn"> struggled</span>
                            )}
                          </span>
                          <span className="small muted">
                            {[
                              session.loggedBy === "family"
                                ? "logged at home"
                                : null,
                              session.autoStopped
                                ? "timer stopped itself"
                                : null,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        </div>
                        <PracticeAttachments
                          base={`/admin/students/${id}/practice/${session.id}/attachments`}
                          attachments={session.attachments}
                        />
                      </div>
                    ))
                  )}
                  {practiceHasMore && (
                    <div className="student-record-history-more">
                      <button
                        type="button"
                        className="quiet"
                        onClick={() => void loadOlderPractice()}
                        disabled={practiceLoading}
                      >
                        {practiceLoading ? "Loading…" : "Load older"}
                      </button>
                      {practiceError && <p className="error small">{practiceError}</p>}
                    </div>
                  )}
                </>
              )}

              {activeTab === "progress" && <StudentProgress studentId={id} />}

              {activeTab === "admin" && (
                <>
                  <div className="student-record-payment-intro">
                    <h2>Student administration</h2>
                    <p className="small muted">
                      Payment-recording, lesson-credit and student-management
                      tools. Bookings are created from Lessons. When staff mark
                      a lesson paid, the Hub records money already received
                      elsewhere; it does not process the payment.
                    </p>
                  </div>

                  <div className="student-record-booking-tools">
                    {showTutorTools && (
                      <TutorStudentTools
                        studentId={id}
                        studentName={student.fullName}
                        showBlockDetails={!canUseCreditCorrections}
                        showBooking={false}
                        onChanged={tutorStudentChanged}
                      />
                    )}

                    {canUseCreditCorrections && (
                      <CreditCorrections
                        key={id}
                        studentId={id}
                        studentName={student.fullName}
                        refreshRevision={creditRevision}
                      />
                    )}

                    {!showTutorTools && !canUseCreditCorrections && (
                      <div className="panel">
                        <p className="empty">
                          No payment or lesson-credit tools are available for
                          this account.
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="student-record-admin-details">
                    {creationNotice && <ActionConfirmation variant="success" title="Student created" description={creationNotice} />}
                    <HubAccessPanel
                      showInviteButton={false}
                      studentId={id}
                      studentName={student.fullName}
                      hubAccess={student.hubAccess}
                      hubInvitations={student.hubInvitations}
                      owner={owner}
                      removalEnabled={student.accountAccessRemovalEnabled === true}
                      onChanged={reload}
                    />
                    <StudentDetails
                      studentId={id}
                      student={student}
                      canEdit={owner}
                      onSaved={() => {
                        reload();
                        setCorrections((seen) => seen + 1);
                      }}
                    />
                    {owner && (
                      <BookingPayer studentId={id} correction={corrections} />
                    )}
                    {owner && <TeachingAssignments studentId={id} />}
                    <StudentGrooves studentId={id} onPracticeChanged={() => setPracticeRevision(value => value + 1)} />

                    <NoteRecipients base={`/admin/students/${id}`} />
                  </div>
                </>
              )}
            </>
          )}
        </section>
      ))}
    </div>
  );
}

/**
 * "Paul added this one. Does the name read right?"
 *
 * One button, because the answer is usually yes. When it is not, the edit form
 * in Admin is the answer and clears this by itself.
 */
function UncheckedRecord({
  studentId,
  addedByName,
  onConfirmed,
}: {
  studentId: string;
  addedByName: string;
  onConfirmed: () => void;
}) {
  const publish = usePublishActionOutcome();
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  return (
    <div className="notice">
      <p className="small">
        <strong>{addedByName}</strong> added this student from a lesson. Check
        the name reads as it should — it is the one every list will show.
      </p>
      {problem && <p className="error small">{problem}</p>}
      <div className="row">
        <button
          type="button"
          className="quiet small"
          disabled={saving}
          onClick={() => {
            setSaving(true);
            setProblem(null);
            confirmStudentRecord(studentId)
              .then(() => { publish({ variant: "success", title: "Student record confirmed", description: "The tutor-added record was reviewed." }); onConfirmed(); })
              .catch((cause: unknown) =>
                setProblem(
                  cause instanceof Error
                    ? cause.message
                    : "Could not save that.",
                ),
              )
              .finally(() => setSaving(false));
          }}
        >
          {saving ? "Saving…" : "Looks right"}
        </button>
        <span className="small muted">
          Or correct it in Admin, which does the same.
        </span>
      </div>
    </div>
  );
}
